// src/store/sharedItemStore.ts
/**
 * .tuMetrics {"tu1": 0.80, "tu2": 0.84, "tu3": 5, "notes": "Shared items store: create/update/delete + recipient fan-out; respects save_to_share_finder", "tuVersion": "2.5"}
 * .help "Manages shared items across the org; controls what appears in ShareFinder vs stream-only."
 */
/**
 * Tu Metadata
 * @tuScore 0.80
 * @tuGrade A
 * @tuTime 2.5h
 * @tuPattern single-responsibility, store-pattern
 * @lines 464
 * @complexity 10
 * @lastUpdated 2026-07-17
 *
 * Help Context
 * @tuPurpose "Manages shared items (URLs, files, media) across the organization. Handles creation, updates, deletion, and metadata preservation."
 * @tuHelp "Shared items are created when users share URLs/files. Store manages pagination, filtering, and updates. Metadata (images, logos) is preserved during edits."
 * @tuUserAction "share-item, edit-shared-item, delete-shared-item"
 * @tuRelated ["ShareDialog", "ShareFinder", "StreamMessage"]
 * @tuPrereq "User must be logged in with companyId"
 * @tuOutcome "Shared items available in streams, ShareFinder, and card attachments"
 */
// Revamped Sept 26, 2025
// - Fetches org-wide shared_items + recipients (no team filter here; UI narrows).
// - Maps recipients cleanly and surfaces helper booleans (isPublic, isMine, isSharedWithMe) for UI logic.
// - Preserves file uploads to 'shared-items' bucket.
// - Posts a lightweight comment event after share via useCommentStore (same as your prior flow).
// December 23, 2025: FRICTIONLESS URL SHARING - Enhanced metadata preservation during updates

import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import useUserStore from './userStore';
import useStreamStore from './streamStore';
import type { Database } from '../types/supabase';
import type { SharedItem } from '../types';
import {
  fetchSharedItems, // NEW: Import fetchSharedItems
  updateSharedItemById, // NEW: Import updateSharedItemById
  deleteSharedItemById, // NEW: Import deleteSharedItemById
  mapSharedItemFromDB, // NEW: Import mapSharedItemFromDB
} from '../lib/supabase/queries/share-queries'; // NEW: Import from new queries file

// ---------- DB row/insert/update types ----------
type SharedItemRow = Database['public']['Tables']['shared_items']['Row'];
type RecipientRow = Database['public']['Tables']['shared_item_recipients']['Row'];

type Recipient =
  | { recipient_type: 'user'; recipient_id: string }
  | { recipient_type: 'team'; recipient_id: string }
  | { recipient_type: 'company'; recipient_id: string }
  | { recipient_type: 'card'; recipient_id: string }
  | { recipient_type: 'standup'; recipient_id: string }; // December 4, 2025: Share to standup meetings

// ---------- Local helpers ----------
const mapSharedItemAndRecipientsFromDB = (
  row: SharedItemRow & { shared_item_recipients?: Pick<RecipientRow, 'recipient_type' | 'recipient_id'>[] }
): SharedItem & {
  recipients: Recipient[];
  isExplicitlySharedWithCurrentUser: boolean; // Renamed from isPublic, isMine, isSharedWithMe
} => {
  const recipients: Recipient[] = (row.shared_item_recipients ?? []).map((r: Pick<RecipientRow, 'recipient_type' | 'recipient_id'>) => ({
    recipient_type: r.recipient_type as Recipient['recipient_type'],
    recipient_id: r.recipient_id as string,
  })) as Recipient[];

  const currentUser = useUserStore.getState().currentUser;

  let isExplicitlySharedWithCurrentUser = false;
  if (currentUser) {
    // "Explicitly shared" means ONLY direct user recipient (not company-wide, not team)
    // This flag is used for "Shared With Me" filter
    // Company-wide and team shares are "public" and should only show in "All"
    
    for (const recipient of recipients) {
      if (recipient.recipient_type === 'user' && recipient.recipient_id === currentUser.id) {
        isExplicitlySharedWithCurrentUser = true;
        break;
      }
      // Note: We do NOT set the flag for team or company recipients
      // Those are considered "public shares" and show only in "All"
    }
  }

  return {
    ...mapSharedItemFromDB(row), // Use the base mapping from share-queries.ts
    recipients: recipients as SharedItem['recipients'] & Recipient[],
    isExplicitlySharedWithCurrentUser,
  };
};

// ---------- Store ----------
interface SharedItemState {
  sharedItems: (SharedItem & {
    recipients: Recipient[];
    isExplicitlySharedWithCurrentUser: boolean;
  })[];
  isLoading: boolean;
  error: string | null;
  hasMoreItems: boolean; // PERFORMANCE: Track if more items available
  isLoadingMore: boolean; // PERFORMANCE: Track loading more state
  /** Last team filter passed to initializeSharedItems (used on soft refresh) */
  selectedTeamId: string | null;

  initializeSharedItems: (selectedTeamId: string | null) => Promise<void>;
  loadMoreSharedItems: () => Promise<void>; // PERFORMANCE: Load more items
  addSharedItem: (
    item: Omit<SharedItem, 'id' | 'createdAt' | 'companyId' | 'createdById'> & { file?: File; urlMetadataId?: string; driveFileMetadataId?: string },
    recipients: Array<{ id: string; type: Recipient['recipient_type'] }>,
    cardId?: string,
    requirementId?: string
  ) => Promise<void>;
  updateSharedItem: (sharedItemId: string, updates: Partial<SharedItem>) => Promise<void>;
  deleteSharedItem: (sharedItemId: string) => Promise<void>;
  /**
   * Keep an item in memory for TipTap MediaNodeView / card embeds even when it is
   * not catalogued in ShareFinder (save_to_share_finder = false).
   */
  cacheSharedItemForRender: (item: SharedItem) => void;
}

// ============================================
// PERFORMANCE OPTIMIZATION: Pagination
// ============================================
// Strategy: Load 50 shared items initially, with Load More functionality.
// ============================================
const SHARED_ITEMS_PER_PAGE = 50;

const useSharedItemStore = create<SharedItemState>((set, get) => ({
  sharedItems: [],
  isLoading: false,
  error: null,
  hasMoreItems: false,
  isLoadingMore: false,
  selectedTeamId: null,

  // Org-wide fetch; UI (Share Finder) applies scope, team, creator, tags, search filters.
  initializeSharedItems: async (selectedTeamId) => {
    set({ isLoading: true, error: null, selectedTeamId });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) {
        set({ sharedItems: [], isLoading: false, hasMoreItems: false });
        return;
      }

      // PERFORMANCE: Fetch first page (50 items)
      const data = await fetchSharedItems(currentUser.companyId, { limit: SHARED_ITEMS_PER_PAGE });

      const mapped =
        (data ?? []).map((row: any) =>
          mapSharedItemAndRecipientsFromDB(row as SharedItemRow & { shared_item_recipients?: RecipientRow[] })
        );

      // Check if there are more items (if we got exactly PER_PAGE, there might be more)
      set({ 
        sharedItems: mapped, 
        isLoading: false,
        hasMoreItems: mapped.length === SHARED_ITEMS_PER_PAGE
      });
    } catch (e: any) {
      console.error('initializeSharedItems error:', e);
      set({ error: e.message ?? 'Failed to load shared items', isLoading: false, hasMoreItems: false });
    }
  },

  // Load more shared items (pagination)
  loadMoreSharedItems: async () => {
    const { sharedItems, isLoadingMore } = get();
    if (isLoadingMore) return;

    set({ isLoadingMore: true });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) {
        set({ isLoadingMore: false });
        return;
      }

      // Fetch next page
      const data = await fetchSharedItems(currentUser.companyId, {
        limit: SHARED_ITEMS_PER_PAGE,
        offset: sharedItems.length
      });

      const mapped =
        (data ?? []).map((row: any) =>
          mapSharedItemAndRecipientsFromDB(row as SharedItemRow & { shared_item_recipients?: RecipientRow[] })
        );

      // Check if there are more items
      set({
        sharedItems: [...sharedItems, ...mapped],
        hasMoreItems: mapped.length === SHARED_ITEMS_PER_PAGE,
        isLoadingMore: false
      });
    } catch (e: any) {
      console.error('loadMoreSharedItems error:', e);
      set({ error: e.message ?? 'Failed to load more shared items', isLoadingMore: false });
    }
  },

  addSharedItem: async (item, recipients, cardId, requirementId) => {
    console.log('🔵 sharedItemStore.addSharedItem called with:', {
      item,
      recipients,
      cardId,
      requirementId,
      recipientsCount: recipients?.length || 0
    });
    
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');
      if (!currentUser.companyId) throw new Error('User is not associated with a company.');

      // Resolve file → public URL if provided
      let finalUrl = item.url ?? '';
      let finalType = item.type;

      if (item.file) {
        const sanitized = item.file.name.replace(/\s/g, '_').replace(/[^a-zA-Z0-9_.-]/g, '');
        const filePath = `${currentUser.companyId}/${currentUser.id}/${Date.now()}-${sanitized}`;

        const { error: uploadError } = await supabase.storage.from('shared-items').upload(filePath, item.file, {
          cacheControl: '3600',
          upsert: false,
        });
        if (uploadError) throw uploadError;

        const { data: publicUrlData } = supabase.storage.from('shared-items').getPublicUrl(filePath);
        finalUrl = publicUrlData.publicUrl;
        finalType = item.type;
      } else if (!item.url) {
        throw new Error('Either a file or a URL must be provided.');
      }

      // Edge Function payload
      const payload = {
        shared_item_data: {
          title: item.title,
          description: item.description ?? null,
          url: finalUrl,
          type: finalType,
          tags: item.tags,
          company_id: currentUser.companyId,
          created_by_id: currentUser.id,
          team_id: item.teamId ?? (currentUser as any).team ?? null,
          card_id: cardId ?? null,
          requirement_id: requirementId ?? null,
          url_metadata_id: item.urlMetadataId ?? null, // October 29, 2025: Link to extracted metadata
          drive_file_metadata_id: item.driveFileMetadataId ?? null, // November 19, 2025: Link to Drive file metadata
          // June 2026: Opt-in only — omit flag defaults to stream-only (not ShareFinder).
          save_to_share_finder: item.saveToShareFinder ?? false,
        },
        recipients: recipients.map((r) => ({
          recipient_type: r.type,
          recipient_id: r.id,
        })),
      };

      console.log('🔵 Calling create-shared-item edge function with payload:', {
        ...payload,
        recipients_count: payload.recipients.length,
        recipients_preview: payload.recipients
      });

      // Call EF to insert item + recipients + emit feed
      const { data, error } = await supabase.functions.invoke('create-shared-item', {
        body: payload,
      });
      
      console.log('🔵 Edge function response:', { data, error });
      if (error) throw error;

      const newId: string | undefined = data?.shared_item_id;
      if (!newId) {
        // Fallback: soft refresh
        await get().initializeSharedItems(get().selectedTeamId);
        set({ isLoading: false });
        return;
      }

      // Fetch the created item w/ recipients and metadata for consistent mapping
      const { data: createdRow, error: fetchErr } = await supabase
        .from('shared_items')
        .select(
          `
          *,
          shared_item_recipients!left(recipient_type, recipient_id),
          url_metadata(*),
          drive_file_metadata(*)
        `
        )
        .eq('id', newId)
        .single();

      if (fetchErr || !createdRow) {
        await get().initializeSharedItems(get().selectedTeamId);
        set({ isLoading: false });
        return;
      }

      const mapped = mapSharedItemAndRecipientsFromDB(
        createdRow as SharedItemRow & { shared_item_recipients?: RecipientRow[] }
      );

      // Create stream messages for each recipient
      // Each user recipient gets a direct message in their chat stream from the sender
      // This ensures consistency: things shared with people use the stream
      const streamStore = useStreamStore.getState();

      // Create stream messages for each recipient
      const streamMessagePromises = recipients.map(async (recipient) => {
        try {
          let streamType: 'direct_message' | 'team_stream' | 'company_stream' | 'standup_stream' | 'artifact_comment';
          let recipientStreamId: string;

          if (recipient.type === 'user') {
            // Each user gets a direct message
            streamType = 'direct_message';
            recipientStreamId = recipient.id;
          } else if (recipient.type === 'team') {
            streamType = 'team_stream';
            recipientStreamId = recipient.id;
          } else if (recipient.type === 'company') {
            streamType = 'company_stream';
            recipientStreamId = recipient.id;
          } else if (recipient.type === 'standup') {
            // December 4, 2025: Share to standup/meeting streams
            streamType = 'standup_stream';
            recipientStreamId = recipient.id;
            console.log('🔵 [SHARE-TO-STANDUP] Preparing to post message:', {
              streamType,
              recipientStreamId,
              recipientId: recipient.id,
              title: mapped.title
            });
          } else if (recipient.type === 'card' && cardId) {
            streamType = 'artifact_comment';
            recipientStreamId = cardId;
          } else {
            // Skip unknown recipient types
            return;
          }

          // Use streamStore.addStreamMessage for direct messages and stream messages
          // This ensures proper stream handling and feed events
          console.log(`🔵 [SHARE] Posting to ${streamType}:`, { recipientStreamId, title: mapped.title });
          await streamStore.addStreamMessage({
            streamType,
            recipientStreamId,
            content: `Shared: ${mapped.title}`,
            sharedItemId: mapped.id,
            sharedItemDetails: {
              title: mapped.title,
              url: mapped.url,
              type: mapped.type,
              description: mapped.description,
              tags: mapped.tags,
              urlMetadata: (mapped as any).urlMetadata || undefined,
              driveFileMetadata: (mapped as any).driveFileMetadata || undefined, // November 20, 2025
            },
            mentions: [],
            tags: [],
          });
          console.log(`✅ [SHARE] Successfully posted to ${streamType}:`, { recipientStreamId });
        } catch (postErr) {
          console.error(`❌ [SHARE] Failed to post share comment to ${recipient.type} ${recipient.id}:`, postErr);
        }
      });

      // Wait for all stream messages to be created
      await Promise.allSettled(streamMessagePromises);

      // Always cache for in-app render (TipTap media nodes). ShareFinder UI/fetch
      // still filters on save_to_share_finder — do not gate store presence on that flag.
      set((state) => ({
        sharedItems: [mapped, ...state.sharedItems.filter((i) => i.id !== mapped.id)],
        isLoading: false,
      }));
    } catch (e: any) {
      console.error('addSharedItem error:', e);
      set({ error: e.message ?? 'Failed to add shared item', isLoading: false });
    }
  },

  cacheSharedItemForRender: (item) => {
    if (!item?.id) return;
    const cached = {
      ...item,
      recipients: (item as { recipients?: Recipient[] }).recipients ?? [],
      isExplicitlySharedWithCurrentUser:
        (item as { isExplicitlySharedWithCurrentUser?: boolean }).isExplicitlySharedWithCurrentUser ?? false,
    };
    set((state) => ({
      sharedItems: [cached, ...state.sharedItems.filter((i) => i.id !== item.id)],
    }));
  },

  updateSharedItem: async (sharedItemId, updates) => {
    set({ isLoading: true, error: null });
    try {
      // NEW: Use updateSharedItemById from share-queries.ts
      const data = await updateSharedItemById(sharedItemId, updates);

      const mapped = mapSharedItemAndRecipientsFromDB(
        data as SharedItemRow & { shared_item_recipients?: RecipientRow[] }
      );

      // December 23, 2025: FRICTIONLESS URL SHARING - Update messages that reference this shared item
      // Update all comments that have this shared_item_id to reflect the new title/description/tags
      // CRITICAL: Fetch fresh metadata from shared_items table to preserve rich preview data
      try {
        const { data: messages, error: messagesError } = await supabase
          .from('comments')
          .select('id, shared_item_details')
          .eq('shared_item_id', sharedItemId);

        if (!messagesError && messages && messages.length > 0) {
          // Fetch the updated shared item with full metadata joins to get fresh metadata
          const { data: freshSharedItem, error: freshError } = await supabase
            .from('shared_items')
            .select(`
              *,
              url_metadata(*),
              drive_file_metadata(*)
            `)
            .eq('id', sharedItemId)
            .single();

          if (freshError) {
            console.error('Error fetching fresh shared item metadata:', freshError);
          }

          // Extract metadata from fresh shared item (handles array/object format)
          let urlMetadata = null;
          if (freshSharedItem?.url_metadata) {
            urlMetadata = Array.isArray(freshSharedItem.url_metadata) 
              ? freshSharedItem.url_metadata[0] 
              : freshSharedItem.url_metadata;
          }

          let driveFileMetadata = null;
          if (freshSharedItem?.drive_file_metadata) {
            driveFileMetadata = Array.isArray(freshSharedItem.drive_file_metadata)
              ? freshSharedItem.drive_file_metadata[0]
              : freshSharedItem.drive_file_metadata;
          }

          // Update each message's shared_item_details to include the new title/description/tags
          // while preserving metadata from the shared_items table
          for (const message of messages) {
            // Parse current details (might be JSON string or object)
            let currentDetails: any = {};
            if (message.shared_item_details) {
              if (typeof message.shared_item_details === 'string') {
                try {
                  currentDetails = JSON.parse(message.shared_item_details);
                } catch (e) {
                  console.error('Error parsing shared_item_details:', e);
                  currentDetails = {};
                }
              } else {
                currentDetails = message.shared_item_details;
              }
            }

            // Build updated details - use fresh metadata from shared_items table
            const updatedDetails = {
              ...currentDetails,
              // Update title/description/tags from database
              title: updates.title !== undefined ? updates.title : (freshSharedItem?.title || currentDetails.title),
              description: updates.description !== undefined ? (updates.description || null) : (freshSharedItem?.description || currentDetails.description),
              tags: updates.tags !== undefined ? updates.tags : (freshSharedItem?.tags || currentDetails.tags),
              // CRITICAL: Use fresh metadata from shared_items table, not stale data from currentDetails
              urlMetadata: urlMetadata || currentDetails.urlMetadata, // Fallback to current if fresh fetch failed
              driveFileMetadata: driveFileMetadata || currentDetails.driveFileMetadata, // Fallback to current if fresh fetch failed
              type: freshSharedItem?.type || currentDetails.type,
              url: freshSharedItem?.url || currentDetails.url,
            };

            await supabase
              .from('comments')
              .update({ 
                shared_item_details: updatedDetails, // Supabase will JSON.stringify automatically
                updated_at: new Date().toISOString()
              })
              .eq('id', message.id);

            // Update stream store if message is in memory
            const streamStore = useStreamStore.getState();
            if (streamStore.messages.some(m => m.id === message.id)) {
              await streamStore.updateStreamMessageSharedItemDetails(message.id, updatedDetails);
            }
          }
          console.log(`✅ Updated ${messages.length} messages with new shared item data and preserved metadata`);
        }
      } catch (updateMessagesError: any) {
        console.error('Error updating messages with shared item changes:', updateMessagesError);
        // Don't fail the whole operation if message updates fail
      }

      set((state) => ({
        sharedItems: state.sharedItems.map((i) => (i.id === sharedItemId ? mapped : i)),
        isLoading: false,
      }));
    } catch (e: any) {
      console.error('updateSharedItem error:', e);
      set({ error: e.message ?? 'Failed to update shared item', isLoading: false });
    }
  },

  deleteSharedItem: async (sharedItemId) => {
    set({ isLoading: true, error: null });
    try {
      // NEW: Use deleteSharedItemById from share-queries.ts
      await deleteSharedItemById(sharedItemId);

      set((state) => ({
        sharedItems: state.sharedItems.filter((i) => i.id !== sharedItemId),
        isLoading: false,
      }));
    } catch (e: any) {
      console.error('deleteSharedItem error:', e);
      set({ error: e.message ?? 'Failed to delete shared item', isLoading: false });
    }
  },
}));

export default useSharedItemStore;
